import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Grid, Card, Typography, CircularProgress } from "@mui/material";
import TeamChart from "../TeamPerformanceChart";
import CommonKpiCharts from "../CommonKpis";
import { getTeamData } from "services/api";
import { getCommonKpis } from "services/api";
import { useTranslation } from "react-i18next";
const getGradientColor = (finalResult) => {
  if (finalResult == null) return "#9E9E9E"; // Grey (for null)

  if (finalResult > 105) return "#1565C0"; // Deep Blue
  if (finalResult >= 90 && finalResult <= 105) return "#2E7D32"; // Dark Green
  if (finalResult >= 80 && finalResult < 90) return "#E65100"; // Dark Orange
  if (finalResult >= 70 && finalResult < 80) return "#FBC02D"; // Dark Yellow
  return "#B71C1C"; // Dark Red
};

const CombinedCharts = ({ month, year, employee }) => {
  const { t } = useTranslation();
  const [kpiData, setKpiData] = useState({});
  const [perspectiveData, setPerspectiveData] = useState({});
  const [commonLoading, setCommonLoading] = useState(false);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [max, setMax] = useState(120);
  useEffect(() => {
    setPerformanceLoading(true);
    setCommonLoading(true);

    getTeamData(month, year, employee).then((data) => {
      if (data == "error") {
        setPerformanceLoading(false);

        return;
      }
      const parsedData = data.map((entry) => {
        const [id, name, avgResult] = entry.split(",");
        return {
          id,
          name,
          avgResult: parseFloat(avgResult),
        };
      });

      setChartData({
        labels: parsedData.map((emp) => emp.name),
        datasets: [
          {
            label: t("user.myTeam.avgResult"),
            data: parsedData.map((emp) => emp.avgResult),
            backgroundColor: parsedData.map((emp) => getGradientColor(emp.avgResult)), // Gradient-based colors
            borderRadius: 8, // Rounded edges
            barThickness: 40, // Thinner bars for a better look
          },
        ],
      });

      setMax(Math.max(100, Math.max(...parsedData.map((emp) => emp.avgResult))));
      setPerformanceLoading(false);
    });

    getCommonKpis(month, year, employee).then((data) => {
      if (data === "error") {
        setCommonLoading(false);
        return;
      }

      const formattedData = data
        .map((entry) => {
          const [name, kpiName, ach, , perspectiveName] = entry.split(",");
          return {
            name,
            kpiName,
            achievement: Number(ach),
            perspective: perspectiveName,
          };
        })
        .filter((entry) => entry.kpiName && entry.name && !isNaN(entry.achievement));

      // --- Filtered KPI Grouping ---
      const groupedByKpi = formattedData.reduce((acc, { name, kpiName, achievement }) => {
        if (!acc[kpiName]) acc[kpiName] = [];
        acc[kpiName].push({ name, achievement });
        return acc;
      }, {});
      // Remove empty or invalid groups
      const filteredKpiData = Object.fromEntries(
        Object.entries(groupedByKpi).filter(([_, entries]) => entries.length > 0)
      );
      setKpiData(filteredKpiData);

      // --- Filtered Perspective Grouping ---
      const groupedByPerspective = formattedData.reduce(
        (acc, { kpiName, achievement, perspective }) => {
          if (!perspective || !kpiName || isNaN(achievement)) return acc;
          if (!acc[perspective]) acc[perspective] = {};
          if (!acc[perspective][kpiName]) acc[perspective][kpiName] = [];
          acc[perspective][kpiName].push(achievement);
          return acc;
        },
        {}
      );
      // Remove empty perspectives or KPIs inside them
      const filteredPerspectiveData = Object.fromEntries(
        Object.entries(groupedByPerspective)
          .map(([perspective, kpis]) => {
            const validKpis = Object.fromEntries(
              Object.entries(kpis).filter(([_, scores]) => scores.length > 0)
            );
            return [perspective, validKpis];
          })
          .filter(([_, kpis]) => Object.keys(kpis).length > 0)
      );
      setPerspectiveData(filteredPerspectiveData);

      setCommonLoading(false);
    });
  }, [month, year, employee]);

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Grid container spacing={3}>
        {/* Team Performance Chart (Top) */}
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 2, height: "100%", boxShadow: 3, borderRadius: 2 }}>
            {performanceLoading ? (
              <Box display="flex" justifyContent="center" p={1}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              chartData && <TeamChart chartData={chartData} max={max} />
            )}
          </Card>
        </Grid>

        {/* Common KPIs Chart (Bottom) */}
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 2, height: "100%", boxShadow: 3, borderRadius: 2 }}>
            {commonLoading ? (
              <Box display="flex" justifyContent="center" p={1}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <CommonKpiCharts kpiData={kpiData} perspectiveData={perspectiveData} />
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

CombinedCharts.propTypes = {
  month: PropTypes.number.isRequired,
  year: PropTypes.number.isRequired,
  employee: PropTypes.string.isRequired,
};

export default CombinedCharts;
